/**
 * Typed fetch client for the Horizon backend.
 *
 * - All requests use credentials: "include" so the session cookie travels.
 * - Vite proxies /api/* → http://localhost:8000 in dev (see vite.config.ts).
 * - In production behind a reverse proxy, /api is same-origin and this works
 *   unchanged.
 */

export type Risk = "high" | "medium" | "low";
export type WorkspaceDomain =
  | "geopolitical"
  | "supply_chain"
  | "financial"
  | "threat"
  | "compliance"
  | "healthcare"
  | "custom";

export interface ApiUser {
  id: string;
  email: string;
  role: "owner" | "admin" | "supervisor" | "operator" | "analyst" | "publisher" | "viewer";
  seat_id: string | null;
  is_dravian_staff?: boolean;
  organization_id?: string | null;
}

export type TeamRole =
  | "owner"
  | "admin"
  | "supervisor"
  | "operator"
  | "analyst"
  | "publisher"
  | "viewer";

export interface TeamMember {
  id: string;
  email: string;
  role: TeamRole | string;
  seat_id: string | null;
  organization_id: string;
  last_seen_at: string;
  workspace_count: number;
}

export interface TeamSummary {
  organization_id: string;
  seats_used: number;
  seats_total: number | null;
  members: TeamMember[];
}

export interface AuditEvent {
  id: number;
  organization_id: string;
  workspace_id: string | null;
  actor_user_id: string | null;
  actor_email: string | null;
  action: string;
  object_type: string;
  object_id: string | null;
  metadata: Record<string, unknown>;
  created_at: string;
}

export interface WorkspaceMember {
  user_id: string;
  email: string;
  role: TeamRole | string;
  last_seen_at: string;
}

export interface ApiWorkspaceStats {
  entities: number;
  relationships: number;
  signals: number;
}

export type ShareOrigin = "owner" | "org_admin" | "explicit_member" | "org_shared";

export interface ApiWorkspace {
  id: string;
  name: string;
  domain: WorkspaceDomain;
  description: string | null;
  ontology_id: string | null;
  owner_user_id: string;
  created_at: string;
  updated_at: string;
  stats?: ApiWorkspaceStats | null;
  // Phase 6 — org-wide live sharing config. Both optional so older API
  // responses don't break the type contract.
  org_shared?: boolean;
  org_shared_default_role?: "viewer" | "analyst";
  effective_role?: TeamRole | null;
  share_origin?: ShareOrigin | null;
}

export interface ApiEntity {
  id: string;
  workspace_id: string;
  type: string;
  name: string;
  attributes: Record<string, unknown>;
  geolocation?: { lat: number; lng: number; region?: string | null } | null;
  color?: string | null;
  operational_area?:
    | GeoJSON.Polygon
    | GeoJSON.MultiPolygon
    | GeoJSON.LineString
    | GeoJSON.MultiLineString
    | null;
  source: string;
  confidence: number;
  created_at: string;
  updated_at: string;
  // Server returns flattened lat/lng on the node, not a nested geolocation —
  // tolerate both.
  lat?: number | null;
  lng?: number | null;
  region?: string | null;
}

export interface ApiRelationship {
  id: string;
  workspace_id: string;
  type: string;
  source_entity_id: string;
  target_entity_id: string;
  attributes: Record<string, unknown>;
  source: string;
  confidence: number;
}

export type ScenarioNodeKind = "initial" | "state" | "event" | "end";
export type Impact = "low" | "medium" | "high" | "critical";

export interface ApiScenarioNode {
  id: string;
  label: string;
  kind: ScenarioNodeKind;
  probability: number;
  impact: Impact | null;
  summary: string | null;
  children: ApiScenarioNode[];
}

export interface ApiScenario {
  id: string;
  workspace_id: string;
  name: string;
  description: string | null;
  horizon_days: number;
  initial_event: string;
  tree: ApiScenarioNode;
  created_at: string;
}

export interface ApiSignal {
  id: string;
  workspace_id: string;
  type: string;
  timestamp: string;
  related_entity_ids: string[];
  attributes: Record<string, unknown>;
  source: string;
  confidence: number;
}

export interface OntologyEntityType {
  name: string;
  description?: string | null;
  example?: string | null;
  attribute_schema: Record<string, string>;
  extends?: string | null;
}

export interface OntologyRelationshipType {
  name: string;
  description?: string | null;
  example?: string | null;
  source_types: string[];
  target_types: string[];
  attribute_schema?: Record<string, string>;
}

export interface OntologySignalType {
  name: string;
  description?: string | null;
  example?: string | null;
  related_entity_types: string[];
  attribute_schema?: Record<string, string>;
}

export interface ApiOntology {
  id: string;
  name: string;
  version: string;
  description?: string | null;
  source?: "shipped" | "user";
  entity_types: OntologyEntityType[];
  relationship_types: OntologyRelationshipType[];
  signal_types: OntologySignalType[];
}

export interface OntologyUsage {
  workspace_count: number;
  workspace_ids: string[];
}

export interface ChatTurn {
  role: "user" | "assistant";
  content: string;
}

export interface ChatRequest {
  message: string;
  history: ChatTurn[];
  thread_id?: string | null;
  persona_id?: string | null;
}

export interface ChatResponse {
  intent: "query" | "modify" | "explain" | "clarify";
  reply: string;
  created_entity_ids: string[];
  created_relationship_ids: string[];
  created_signal_ids: string[];
  created_scenario_ids: string[];
  merged_entity_pairs: { src: string; tgt: string }[];
  matched_entity_ids: string[];
  recolored_entity_ids: string[];
  areas_set_entity_ids: string[];
  focus_entity_ids: string[];
  deleted_entity_ids: string[];
  deleted_signal_ids: string[];
  export_url: string | null;
  thread_id: string | null;
  thread_title: string | null;
  raw_action: Record<string, unknown>;
}

export interface ChatThread {
  id: string;
  workspace_id: string;
  title: string;
  parent_thread_id: string | null;
  persona_id: string | null;
  created_at: string;
  updated_at: string;
  message_count?: number;
  last_message_preview?: string | null;
}

export interface ChatMessage {
  id: string;
  thread_id: string;
  role: "user" | "assistant";
  content: string;
  meta: Record<string, unknown>;
  created_at: string;
}

export interface ChatPersona {
  id: string;
  workspace_id: string;
  name: string;
  icon: string;
  description: string | null;
  system_instructions: string;
  starter_prompts: string[];
  source: "shipped" | "user";
  created_at: string;
  updated_at: string;
}

export class ApiError extends Error {
  constructor(public status: number, public body: unknown, message?: string) {
    super(message ?? `API ${status}`);
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  // Lazily import to avoid a TS circular ref between api.ts and auth.tsx — the
  // auth module imports nothing from api.ts.
  const { getAccessToken } = await import("@/lib/auth");

  const send = async (bearer: string | null) => {
    return fetch(`/api${path}`, {
      credentials: "include",
      headers: {
        "content-type": "application/json",
        ...(bearer ? { authorization: `Bearer ${bearer}` } : {}),
        ...(init?.headers ?? {}),
      },
      ...init,
    });
  };

  let res = await send(getAccessToken());

  // Cognito access tokens expire (default 1h). If the backend tells us the
  // token is expired, try to refresh once and retry the request before
  // surfacing the error. Avoids losing in-flight UI state (e.g. wizard).
  if (res.status === 401) {
    let detail = "";
    try {
      const peek = res.clone();
      const json = await peek.json();
      detail = String(json?.detail ?? "");
    } catch {
      /* not JSON, ignore */
    }
    if (detail.toLowerCase().includes("expired")) {
      try {
        const m = await import("@/lib/cognito-srp");
        const refreshed = await m.srpRefreshIfPossible();
        if (refreshed) {
          res = await send(getAccessToken());
        }
      } catch {
        /* refresh failed — let the original 401 propagate */
      }
    }
  }

  if (res.status === 204) return undefined as T;
  const body = res.headers.get("content-type")?.includes("application/json")
    ? await res.json()
    : await res.text();
  if (!res.ok) {
    throw new ApiError(res.status, body, typeof body === "string" ? body : JSON.stringify(body));
  }
  return body as T;
}

// --- Auth ---

export const auth = {
  login(email: string, password: string) {
    return request<{ user: ApiUser; license_tier: string; license_expires_at: string }>(
      "/auth/login",
      { method: "POST", body: JSON.stringify({ email, password }) },
    );
  },
  logout() {
    return request<void>("/auth/logout", { method: "POST" });
  },
  me() {
    return request<ApiUser>("/auth/me");
  },
};

// --- Workspaces ---

export const workspaces = {
  list() {
    return request<ApiWorkspace[]>("/workspaces");
  },
  get(id: string) {
    return request<ApiWorkspace>(`/workspaces/${id}`);
  },
  create(payload: { name: string; domain: WorkspaceDomain; description?: string; ontology_id?: string | null }) {
    return request<ApiWorkspace>("/workspaces", {
      method: "POST",
      body: JSON.stringify(payload),
    });
  },
  update(id: string, patch: { name?: string; description?: string; domain?: WorkspaceDomain }) {
    return request<ApiWorkspace>(`/workspaces/${id}`, {
      method: "PATCH",
      body: JSON.stringify(patch),
    });
  },
  remove(id: string) {
    return request<void>(`/workspaces/${id}`, { method: "DELETE" });
  },
  updateSharing(
    id: string,
    payload: { org_shared?: boolean; org_shared_default_role?: "viewer" | "analyst" },
  ) {
    return request<ApiWorkspace>(`/workspaces/${id}/sharing`, {
      method: "PATCH",
      body: JSON.stringify(payload),
    });
  },
};

// --- Entities ---

export const entities = {
  /**
   * `type`: restrict to a single entity type.
   * `limit`: cap. Backend default is 500 — pass a larger value from views
   *   that need broader coverage (e.g. the Geographic canvas).
   * `hasCoords`: server-side filter to entities whose Neo4j node has
   *   `lat` AND `lng`. The Geographic canvas relies on this so its
   *   finite return window isn't filled with actor entities that
   *   correctly carry no location.
   */
  list(
    workspaceId: string,
    opts?: { type?: string; limit?: number; hasCoords?: boolean } | string,
  ) {
    // Back-compat: `list(id, "type_name")` still works.
    const params = new URLSearchParams();
    if (typeof opts === "string") {
      if (opts) params.set("type", opts);
    } else if (opts) {
      if (opts.type) params.set("type", opts.type);
      if (opts.limit != null) params.set("limit", String(opts.limit));
      if (opts.hasCoords) params.set("has_coords", "true");
    }
    const q = params.toString();
    return request<ApiEntity[]>(
      `/workspaces/${workspaceId}/entities${q ? `?${q}` : ""}`,
    );
  },
  create(workspaceId: string, payload: Partial<ApiEntity> & { type: string; name: string }) {
    return request<ApiEntity>(`/workspaces/${workspaceId}/entities`, {
      method: "POST",
      body: JSON.stringify(payload),
    });
  },
  update(workspaceId: string, entityId: string, patch: Partial<ApiEntity>) {
    return request<ApiEntity>(`/workspaces/${workspaceId}/entities/${entityId}`, {
      method: "PATCH",
      body: JSON.stringify(patch),
    });
  },
};

// --- Relationships ---

export const relationships = {
  list(workspaceId: string) {
    return request<ApiRelationship[]>(`/workspaces/${workspaceId}/relationships`);
  },
  create(
    workspaceId: string,
    payload: {
      type: string;
      source_entity_id: string;
      target_entity_id: string;
      attributes?: Record<string, unknown>;
    },
  ) {
    return request<ApiRelationship>(`/workspaces/${workspaceId}/relationships`, {
      method: "POST",
      body: JSON.stringify(payload),
    });
  },
};

// --- Scenarios ---

export const scenarios = {
  list(workspaceId: string) {
    return request<ApiScenario[]>(`/workspaces/${workspaceId}/scenarios`);
  },
  get(workspaceId: string, scenarioId: string) {
    return request<ApiScenario>(`/workspaces/${workspaceId}/scenarios/${scenarioId}`);
  },
};

// --- Decisions (v1.4) ---

export type ApiDecisionStatus =
  | "draft"
  | "analyzed"
  | "accepted"
  | "rejected"
  | "superseded";
export type ApiDecisionRecommendation =
  | "accept"
  | "reject"
  | "revise"
  | "insufficient_data";
export type ApiConfidenceLabel = "low" | "medium" | "high";
export type ApiDecisionActionStatus =
  | "open"
  | "in_progress"
  | "completed"
  | "blocked"
  | "cancelled";

export interface ApiDecisionRisk {
  label: string;
  severity: "high" | "medium" | "low";
  detail: string | null;
}

export interface ApiDecisionTradeoff {
  label: string;
  detail: string | null;
}

export interface ApiDecisionEvidence {
  entity_ids: string[];
  signal_ids: string[];
  scenario_ids: string[];
}

export interface ApiConfidenceReasons {
  evidence_count: number;
  recency_days: number | null;
  source_diversity: number;
  contradictions: string[];
  gaps: string[];
  insufficient_grounding: boolean;
}

export interface ApiDecision {
  id: string;
  workspace_id: string;
  organization_id: string;
  title: string;
  decision_input: string;
  status: ApiDecisionStatus;
  recommendation: ApiDecisionRecommendation | null;
  summary: string | null;
  rationale: string | null;
  risks: ApiDecisionRisk[];
  tradeoffs: ApiDecisionTradeoff[];
  unknowns: string[];
  evidence: ApiDecisionEvidence;
  confidence_label: ApiConfidenceLabel | null;
  confidence_reasons: ApiConfidenceReasons;
  briefing_markdown: string | null;
  briefing_generated_at: string | null;
  created_by_user_id: string;
  decided_by_user_id: string | null;
  decided_at: string | null;
  analyzed_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface ApiSuggestedAction {
  title: string;
  description: string | null;
  suggested_owner_role: string | null;
}

export interface ApiDecisionAcceptResponse {
  decision: ApiDecision;
  suggested_actions: ApiSuggestedAction[];
  suggestion_error: string | null;
}

export interface ApiDecisionAction {
  id: string;
  decision_id: string;
  workspace_id: string;
  title: string;
  description: string | null;
  owner_user_id: string | null;
  status: ApiDecisionActionStatus;
  due_at: string | null;
  outcome: string | null;
  created_by_user_id: string;
  created_at: string;
  updated_at: string;
}

export interface ApiDecisionChatMessage {
  id: string;
  decision_id: string;
  role: "user" | "assistant";
  content: string;
  meta: Record<string, unknown>;
  created_at: string;
}

export interface ApiDecisionChatResponse {
  user_message: ApiDecisionChatMessage;
  assistant_message: ApiDecisionChatMessage;
}

export interface ApiDecisionBriefingResponse {
  markdown: string;
  generated_at: string;
}

// --- Entity attachments (v1.5.0) ---

export type ApiEntityAttachmentKind = "image" | "document";

export interface ApiEntityAttachment {
  id: string;
  workspace_id: string;
  entity_id: string;
  filename: string;
  mime_type: string;
  size_bytes: number;
  kind: ApiEntityAttachmentKind;
  sha256: string;
  is_primary: boolean;
  uploaded_by_user_id: string;
  uploaded_at: string;
  url: string;
}

export const entityAttachments = {
  list(workspaceId: string, entityId: string) {
    return request<ApiEntityAttachment[]>(
      `/workspaces/${workspaceId}/entities/${entityId}/attachments`,
    );
  },
  // The upload uses multipart FormData, so we bypass the JSON `request`
  // helper and inline the fetch. We still reuse the same auth flow.
  async upload(workspaceId: string, entityId: string, file: File) {
    const { getAccessToken } = await import("@/lib/auth");
    const form = new FormData();
    form.append("file", file);
    const bearer = getAccessToken();
    const res = await fetch(
      `/api/workspaces/${workspaceId}/entities/${entityId}/attachments`,
      {
        method: "POST",
        credentials: "include",
        headers: bearer ? { authorization: `Bearer ${bearer}` } : undefined,
        body: form,
      },
    );
    if (!res.ok) {
      const body = res.headers.get("content-type")?.includes("application/json")
        ? await res.json()
        : await res.text();
      throw new ApiError(
        res.status,
        body,
        typeof body === "string" ? body : JSON.stringify(body),
      );
    }
    return (await res.json()) as ApiEntityAttachment;
  },
  remove(workspaceId: string, entityId: string, attachmentId: string) {
    return request<void>(
      `/workspaces/${workspaceId}/entities/${entityId}/attachments/${attachmentId}`,
      { method: "DELETE" },
    );
  },
  setPrimary(workspaceId: string, entityId: string, attachmentId: string) {
    return request<ApiEntityAttachment>(
      `/workspaces/${workspaceId}/entities/${entityId}/attachments/${attachmentId}/primary`,
      { method: "POST" },
    );
  },
};

export const decisions = {
  list(workspaceId: string) {
    return request<ApiDecision[]>(`/workspaces/${workspaceId}/decisions`);
  },
  get(workspaceId: string, decisionId: string) {
    return request<ApiDecision>(
      `/workspaces/${workspaceId}/decisions/${decisionId}`,
    );
  },
  create(workspaceId: string, payload: { title: string; decision_input: string }) {
    return request<ApiDecision>(`/workspaces/${workspaceId}/decisions`, {
      method: "POST",
      body: JSON.stringify(payload),
    });
  },
  patch(
    workspaceId: string,
    decisionId: string,
    payload: { title?: string; decision_input?: string },
  ) {
    return request<ApiDecision>(
      `/workspaces/${workspaceId}/decisions/${decisionId}`,
      { method: "PATCH", body: JSON.stringify(payload) },
    );
  },
  remove(workspaceId: string, decisionId: string) {
    return request<void>(`/workspaces/${workspaceId}/decisions/${decisionId}`, {
      method: "DELETE",
    });
  },
  analyze(workspaceId: string, decisionId: string) {
    return request<ApiDecision>(
      `/workspaces/${workspaceId}/decisions/${decisionId}/analyze`,
      { method: "POST" },
    );
  },
  accept(workspaceId: string, decisionId: string) {
    return request<ApiDecisionAcceptResponse>(
      `/workspaces/${workspaceId}/decisions/${decisionId}/accept`,
      { method: "POST" },
    );
  },
  reject(workspaceId: string, decisionId: string, reason?: string) {
    return request<ApiDecision>(
      `/workspaces/${workspaceId}/decisions/${decisionId}/reject`,
      { method: "POST", body: JSON.stringify({ reason: reason ?? null }) },
    );
  },
  messages(workspaceId: string, decisionId: string) {
    return request<ApiDecisionChatMessage[]>(
      `/workspaces/${workspaceId}/decisions/${decisionId}/messages`,
    );
  },
  chat(workspaceId: string, decisionId: string, message: string) {
    return request<ApiDecisionChatResponse>(
      `/workspaces/${workspaceId}/decisions/${decisionId}/chat`,
      { method: "POST", body: JSON.stringify({ message }) },
    );
  },
  briefing(workspaceId: string, decisionId: string) {
    return request<ApiDecisionBriefingResponse>(
      `/workspaces/${workspaceId}/decisions/${decisionId}/briefing`,
      { method: "POST" },
    );
  },
  actions: {
    list(workspaceId: string, decisionId: string) {
      return request<ApiDecisionAction[]>(
        `/workspaces/${workspaceId}/decisions/${decisionId}/actions`,
      );
    },
    create(
      workspaceId: string,
      decisionId: string,
      payload: {
        title: string;
        description?: string | null;
        owner_user_id?: string | null;
        due_at?: string | null;
      },
    ) {
      return request<ApiDecisionAction>(
        `/workspaces/${workspaceId}/decisions/${decisionId}/actions`,
        { method: "POST", body: JSON.stringify(payload) },
      );
    },
    patch(
      workspaceId: string,
      decisionId: string,
      actionId: string,
      payload: {
        title?: string;
        description?: string | null;
        owner_user_id?: string | null;
        status?: ApiDecisionActionStatus;
        due_at?: string | null;
        outcome?: string | null;
      },
    ) {
      return request<ApiDecisionAction>(
        `/workspaces/${workspaceId}/decisions/${decisionId}/actions/${actionId}`,
        { method: "PATCH", body: JSON.stringify(payload) },
      );
    },
  },
};

// --- Signals ---

export const signals = {
  list(workspaceId: string, opts?: { entity_id?: string; type?: string }) {
    const params = new URLSearchParams();
    if (opts?.entity_id) params.set("entity_id", opts.entity_id);
    if (opts?.type) params.set("type", opts.type);
    const q = params.toString();
    return request<ApiSignal[]>(`/workspaces/${workspaceId}/signals${q ? `?${q}` : ""}`);
  },
  remove(workspaceId: string, signalId: string) {
    return request<void>(`/workspaces/${workspaceId}/signals/${signalId}`, {
      method: "DELETE",
    });
  },
};

// --- Ontologies ---

export const ontologies = {
  list() {
    return request<ApiOntology[]>("/ontologies");
  },
  get(ontologyId: string) {
    return request<ApiOntology>(`/ontologies/${ontologyId}`);
  },
  usage(ontologyId: string) {
    return request<OntologyUsage>(`/ontologies/${ontologyId}/usage`);
  },
  create(payload: {
    name: string;
    version?: string;
    description?: string | null;
    entity_types?: OntologyEntityType[];
    relationship_types?: OntologyRelationshipType[];
    signal_types?: OntologySignalType[];
  }) {
    return request<ApiOntology>("/ontologies", {
      method: "POST",
      body: JSON.stringify(payload),
    });
  },
  update(
    ontologyId: string,
    patch: Partial<{
      name: string;
      version: string;
      description: string | null;
      entity_types: OntologyEntityType[];
      relationship_types: OntologyRelationshipType[];
      signal_types: OntologySignalType[];
    }>,
  ) {
    return request<ApiOntology>(`/ontologies/${ontologyId}`, {
      method: "PUT",
      body: JSON.stringify(patch),
    });
  },
  remove(ontologyId: string) {
    return request<void>(`/ontologies/${ontologyId}`, { method: "DELETE" });
  },
  assign(ontologyId: string, workspaceId: string) {
    return request<void>(`/ontologies/${ontologyId}/assign/${workspaceId}`, { method: "POST" });
  },
};

// --- Ingestion ---

export interface IngestionResult {
  entities_created: number;
  entities_resolved: number;
  relationships_created: number;
  relationships_skipped: number;
  signals_created: number;
  scenarios_created: number;
  errors: string[];
}

export interface CsvPreview {
  columns: string[];
  row_count: number;
  sample_rows: Record<string, string>[];
  suggested_mapping: Record<string, string>;
}

export const ingestion = {
  importJson(workspaceId: string, payload: unknown) {
    return request<IngestionResult>(`/workspaces/${workspaceId}/import/json`, {
      method: "POST",
      body: JSON.stringify(payload),
    });
  },
  async previewCsv(workspaceId: string, file: File) {
    const fd = new FormData();
    fd.append("file", file);
    const res = await fetch(`/api/workspaces/${workspaceId}/import/csv/preview`, {
      method: "POST",
      credentials: "include",
      body: fd,
    });
    if (!res.ok) {
      const body = await res.text();
      throw new ApiError(res.status, body, body || `CSV preview failed (${res.status})`);
    }
    return (await res.json()) as CsvPreview;
  },
  commitCsv(
    workspaceId: string,
    payload: { csv_text: string; mapping: Record<string, string>; default_type?: string },
  ) {
    return request<IngestionResult>(`/workspaces/${workspaceId}/import/csv`, {
      method: "POST",
      body: JSON.stringify(payload),
    });
  },
};

// --- Sources & Ingestion Runs ---

export type SourceKind = "rss" | "rest_api";
export type SourceStatus = "active" | "paused" | "error";
export type RunStatus = "running" | "succeeded" | "failed";

export interface ApiSource {
  id: string;
  workspace_id: string;
  name: string;
  kind: SourceKind;
  config: Record<string, unknown>;
  poll_interval_minutes: number;
  status: SourceStatus;
  last_polled_at: string | null;
  last_error: string | null;
  created_at: string;
}

export interface ApiIngestionRun {
  id: string;
  source_id: string;
  workspace_id: string;
  started_at: string;
  finished_at: string | null;
  status: RunStatus;
  counts: Partial<IngestionResult>;
  articles_seen: number;
  articles_processed: number;
  error: string | null;
}

export const sources = {
  list(workspaceId: string) {
    return request<ApiSource[]>(`/workspaces/${workspaceId}/sources`);
  },
  create(
    workspaceId: string,
    payload: {
      name: string;
      kind: SourceKind;
      config: Record<string, unknown>;
      poll_interval_minutes?: number;
    },
  ) {
    return request<ApiSource>(`/workspaces/${workspaceId}/sources`, {
      method: "POST",
      body: JSON.stringify(payload),
    });
  },
  update(
    workspaceId: string,
    sourceId: string,
    patch: { name?: string; poll_interval_minutes?: number; status?: SourceStatus },
  ) {
    return request<ApiSource>(`/workspaces/${workspaceId}/sources/${sourceId}`, {
      method: "PATCH",
      body: JSON.stringify(patch),
    });
  },
  remove(workspaceId: string, sourceId: string) {
    return request<void>(`/workspaces/${workspaceId}/sources/${sourceId}`, {
      method: "DELETE",
    });
  },
  run(workspaceId: string, sourceId: string) {
    return request<{ status: string }>(
      `/workspaces/${workspaceId}/sources/${sourceId}/run`,
      { method: "POST" },
    );
  },
  runs(workspaceId: string) {
    return request<ApiIngestionRun[]>(`/workspaces/${workspaceId}/sources/runs`);
  },
};

// --- Predictions ---

export interface PredictionRunResult {
  patterns_detected: number;
  hypotheses_proposed: number;
  hypotheses_accepted: number;
  hypotheses_rejected: number;
  rejection_summary: Record<string, number>;
  created_signal_ids: string[];
}

export interface PredictionAttempt {
  id: string;
  generated_at: string;
  hypothesis: Record<string, unknown>;
  rejection_reasons: string[];
  patterns_context: unknown[];
}

export const predictions = {
  run(workspaceId: string) {
    return request<PredictionRunResult>(`/workspaces/${workspaceId}/predictions/run`, {
      method: "POST",
    });
  },
  attempts(workspaceId: string) {
    return request<PredictionAttempt[]>(`/workspaces/${workspaceId}/predictions/attempts`);
  },
  promoteToScenario(workspaceId: string, signalId: string) {
    return request<ApiScenario>(
      `/workspaces/${workspaceId}/predictions/${signalId}/promote-to-scenario`,
      { method: "POST" },
    );
  },
};

// --- Chat ---

// --- License ---

export interface LicenseStatus {
  state: "active" | "grace" | "needs_activation" | "degraded";
  organization_id: string | null;
  organization_name: string | null;
  subscription_tier: string | null;
  subscription_status: string | null;
  expires_at: string | null;
  grace_until: string | null;
  features: string[] | null;
  message: string | null;
}

export const license = {
  status() {
    return request<LicenseStatus>("/license/status");
  },
};

// --- Team & memberships ---

export const team = {
  summary() {
    return request<TeamSummary>("/team");
  },
  activity(limit = 100) {
    return request<AuditEvent[]>(`/team/activity?limit=${limit}`);
  },
  invite(payload: { email: string; name?: string | null; role?: "admin" | "analyst" | "viewer" }) {
    return request<{ status: string; message?: string }>("/team/invite", {
      method: "POST",
      body: JSON.stringify(payload),
    });
  },
  workspaceMembers(workspaceId: string) {
    return request<WorkspaceMember[]>(`/workspaces/${workspaceId}/members`);
  },
  setWorkspaceMember(workspaceId: string, userId: string, role: TeamRole) {
    return request<WorkspaceMember>(`/workspaces/${workspaceId}/members/${userId}`, {
      method: "PUT",
      body: JSON.stringify({ role }),
    });
  },
  removeWorkspaceMember(workspaceId: string, userId: string) {
    return request<void>(`/workspaces/${workspaceId}/members/${userId}`, {
      method: "DELETE",
    });
  },
};

// --- LLM credentials (org-level) ---

export type LlmProviderName = "anthropic" | "openai" | "azure_openai" | "bedrock";

export interface LlmCredential {
  id: string;
  provider: LlmProviderName;
  last4: string;
  extras: Record<string, unknown>;
  is_active: boolean;
}

export const llmCredentials = {
  list() {
    return request<LlmCredential[]>("/llm/credentials");
  },
  upsert(payload: {
    provider: LlmProviderName;
    api_key: string;
    extras?: Record<string, unknown>;
    activate?: boolean;
  }) {
    return request<LlmCredential>("/llm/credentials", {
      method: "PUT",
      body: JSON.stringify(payload),
    });
  },
  activate(provider: LlmProviderName) {
    return request<LlmCredential>("/llm/credentials/activate", {
      method: "POST",
      body: JSON.stringify({ provider }),
    });
  },
  remove(credentialId: string) {
    return request<void>(`/llm/credentials/${credentialId}`, {
      method: "DELETE",
    });
  },
  test(payload: {
    provider: LlmProviderName;
    api_key: string;
    extras?: Record<string, unknown>;
  }) {
    return request<{ ok: boolean; model?: string; sample?: string; error?: string }>(
      "/llm/credentials/test",
      { method: "POST", body: JSON.stringify(payload) },
    );
  },
};

export const chat = {
  send(workspaceId: string, req: ChatRequest) {
    return request<ChatResponse>(`/workspaces/${workspaceId}/chat`, {
      method: "POST",
      body: JSON.stringify(req),
    });
  },
};

export const chatThreads = {
  list(workspaceId: string) {
    return request<ChatThread[]>(`/workspaces/${workspaceId}/chat/threads`);
  },
  create(
    workspaceId: string,
    payload: { title?: string; parent_thread_id?: string | null; persona_id?: string | null } = {},
  ) {
    return request<ChatThread>(`/workspaces/${workspaceId}/chat/threads`, {
      method: "POST",
      body: JSON.stringify(payload),
    });
  },
  messages(workspaceId: string, threadId: string) {
    return request<ChatMessage[]>(
      `/workspaces/${workspaceId}/chat/threads/${threadId}/messages`,
    );
  },
  patch(
    workspaceId: string,
    threadId: string,
    patch: Partial<{ title: string; persona_id: string | null }>,
  ) {
    return request<ChatThread>(`/workspaces/${workspaceId}/chat/threads/${threadId}`, {
      method: "PATCH",
      body: JSON.stringify(patch),
    });
  },
  remove(workspaceId: string, threadId: string) {
    return request<void>(`/workspaces/${workspaceId}/chat/threads/${threadId}`, {
      method: "DELETE",
    });
  },
};

export const chatPersonas = {
  list(workspaceId: string) {
    return request<ChatPersona[]>(`/workspaces/${workspaceId}/chat/personas`);
  },
  create(
    workspaceId: string,
    payload: {
      name: string;
      icon?: string;
      description?: string | null;
      system_instructions?: string;
      starter_prompts?: string[];
    },
  ) {
    return request<ChatPersona>(`/workspaces/${workspaceId}/chat/personas`, {
      method: "POST",
      body: JSON.stringify(payload),
    });
  },
  patch(
    workspaceId: string,
    personaId: string,
    patch: Partial<{
      name: string;
      icon: string;
      description: string | null;
      system_instructions: string;
      starter_prompts: string[];
    }>,
  ) {
    return request<ChatPersona>(`/workspaces/${workspaceId}/chat/personas/${personaId}`, {
      method: "PATCH",
      body: JSON.stringify(patch),
    });
  },
  remove(workspaceId: string, personaId: string) {
    return request<void>(`/workspaces/${workspaceId}/chat/personas/${personaId}`, {
      method: "DELETE",
    });
  },
};

// ---------- onboarding ----------

export type IndustryId =
  | "government"
  | "healthcare"
  | "finance"
  | "defense"
  | "ngo"
  | "energy"
  | "telecom"
  | "other";

export interface OnboardingStatus {
  completed: boolean;
  can_complete: boolean;
  industry: IndustryId | null;
  use_case: string | null;
  share_metadata_with_dravian: boolean;
}

export interface OnboardingCompletePayload {
  industry: IndustryId;
  use_case: string;
  share_metadata_with_dravian: boolean;
}

export const onboarding = {
  status() {
    return request<OnboardingStatus>("/onboarding/status");
  },
  complete(payload: OnboardingCompletePayload) {
    return request<OnboardingStatus>("/onboarding/complete", {
      method: "POST",
      body: JSON.stringify(payload),
    });
  },
};

// ---------- publications (EPIC-13) ----------

export type PublicationMode =
  | "private"
  | "shared_with_user"
  | "org_shared"
  | "link_token"
  | "public";

export type PublicationStatus = "draft" | "active" | "revoked";
export type VersionStatus = "generating" | "draft" | "active" | "archived" | "failed";

export interface Publication {
  id: string;
  workspace_id: string;
  mode: PublicationMode;
  status: PublicationStatus;
  hostname: string | null;
  title: string;
  description: string | null;
  current_version_id: string | null;
  include_entities: boolean;
  include_relationships: boolean;
  include_signals: boolean;
  min_signal_severity: "low" | "medium" | "high" | "critical";
  include_network: boolean;
  include_geo: boolean;
  include_timeline: boolean;
  include_scenarios: boolean;
  include_ontology: boolean;
  include_guided_questions: boolean;
  guided_questions_count: number;
  allow_indexing: boolean;
  created_at: string;
  updated_at: string;
  revoked_at: string | null;
}

export interface PublicationVersion {
  id: string;
  publication_id: string;
  version_number: number;
  status: VersionStatus;
  sanitizer_version: string;
  checksum: string | null;
  snapshot_bytes: number | null;
  strip_counters: Record<string, number>;
  generated_at: string;
  activated_at: string | null;
  error: string | null;
}

export interface CreatePublicationPayload {
  mode: PublicationMode;
  title: string;
  description?: string | null;
  hostname?: string | null;
  include_entities?: boolean;
  include_relationships?: boolean;
  include_signals?: boolean;
  min_signal_severity?: "low" | "medium" | "high" | "critical";
  include_network?: boolean;
  include_geo?: boolean;
  include_timeline?: boolean;
  include_scenarios?: boolean;
  include_ontology?: boolean;
  include_guided_questions?: boolean;
  guided_questions_count?: number;
  allow_indexing?: boolean;
}

export const publications = {
  list(workspaceId: string) {
    return request<Publication[]>(`/workspaces/${workspaceId}/publications`);
  },
  get(workspaceId: string, publicationId: string) {
    return request<Publication>(
      `/workspaces/${workspaceId}/publications/${publicationId}`,
    );
  },
  create(workspaceId: string, payload: CreatePublicationPayload) {
    return request<Publication>(`/workspaces/${workspaceId}/publications`, {
      method: "POST",
      body: JSON.stringify(payload),
    });
  },
  listVersions(workspaceId: string, publicationId: string) {
    return request<PublicationVersion[]>(
      `/workspaces/${workspaceId}/publications/${publicationId}/versions`,
    );
  },
  generateVersion(workspaceId: string, publicationId: string) {
    return request<PublicationVersion>(
      `/workspaces/${workspaceId}/publications/${publicationId}/versions`,
      { method: "POST" },
    );
  },
  async getSnapshot(workspaceId: string, publicationId: string, versionId: string) {
    const { getAccessToken } = await import("@/lib/auth");
    const bearer = getAccessToken();
    const res = await fetch(
      `/api/workspaces/${workspaceId}/publications/${publicationId}/versions/${versionId}/snapshot`,
      {
        credentials: "include",
        headers: bearer ? { authorization: `Bearer ${bearer}` } : {},
      },
    );
    if (!res.ok) {
      const body = await res.text();
      throw new ApiError(res.status, body, body);
    }
    return res.json();
  },
  publishVersion(workspaceId: string, publicationId: string, versionId: string) {
    return request<Publication>(
      `/workspaces/${workspaceId}/publications/${publicationId}/versions/${versionId}/publish`,
      { method: "POST" },
    );
  },
  rollbackVersion(workspaceId: string, publicationId: string, versionId: string) {
    return request<Publication>(
      `/workspaces/${workspaceId}/publications/${publicationId}/versions/${versionId}/rollback`,
      { method: "POST" },
    );
  },
  revoke(workspaceId: string, publicationId: string) {
    return request<Publication>(
      `/workspaces/${workspaceId}/publications/${publicationId}/revoke`,
      { method: "POST" },
    );
  },
};

// ---------- Devices (Phase 1) ----------
//
// Devices are ordinary `field_device` entities in the graph, so listing and
// detail reuse the entities API — there is no device-specific read endpoint.
// What lives here is the part entities can't express: the command queue
// (Horizon -> device) and bridge token administration.

export interface ApiDeviceCommand {
  id: string;
  device_entity_id: string;
  type: string;
  /** pending | delivered | done | failed */
  status: string;
  params: Record<string, unknown>;
  result: string | null;
  created_at: string;
  delivered_at: string | null;
  completed_at: string | null;
  /** Delivered but never acked past the server's threshold. */
  stale: boolean;
}

export const devices = {
  /** Devices are entities; this is a convenience wrapper over entities.list. */
  list(workspaceId: string) {
    return entities.list(workspaceId, { type: "field_device", limit: 500 });
  },
  listCommands(workspaceId: string, deviceId: string, limit = 20) {
    return request<ApiDeviceCommand[]>(
      `/workspaces/${workspaceId}/devices/${deviceId}/commands?limit=${limit}`,
    );
  },
  queueCommand(
    workspaceId: string,
    deviceId: string,
    payload: { type: string; params?: Record<string, unknown> },
  ) {
    return request<ApiDeviceCommand>(
      `/workspaces/${workspaceId}/devices/${deviceId}/commands`,
      { method: "POST", body: JSON.stringify({ params: {}, ...payload }) },
    );
  },
};

export interface ApiBridgeToken {
  id: string;
  workspace_id: string;
  name: string;
  created_by_user_id: string;
  created_at: string;
  revoked_at: string | null;
  last_used_at: string | null;
  /** Hard expiry. `null` only on tokens minted before expiry existed. */
  expires_at: string | null;
  /** Server-evaluated, so the UI never has to trust the browser clock. */
  expired: boolean;
}

/** Everything a bridge needs to configure itself. Rendered as a QR so the
 *  field operator doesn't have to type a 43-character token on a phone. */
export interface ApiBridgeProvisioning {
  v: number;
  api_base: string;
  workspace_id: string;
  token: string;
  expires_at: string | null;
}

/** The raw `token` is returned exactly once, by create. It is never
 *  retrievable afterwards — the server only stores its SHA-256. */
export interface ApiBridgeTokenCreated extends ApiBridgeToken {
  token: string;
  provisioning: ApiBridgeProvisioning;
}

export const bridgeTokens = {
  list(workspaceId: string) {
    return request<ApiBridgeToken[]>(`/workspaces/${workspaceId}/bridge-tokens`);
  },
  create(workspaceId: string, name: string, ttlHours: number) {
    return request<ApiBridgeTokenCreated>(`/workspaces/${workspaceId}/bridge-tokens`, {
      method: "POST",
      body: JSON.stringify({ name, ttl_hours: ttlHours }),
    });
  },
  revoke(workspaceId: string, tokenId: string) {
    return request<void>(`/workspaces/${workspaceId}/bridge-tokens/${tokenId}`, {
      method: "DELETE",
    });
  },
};
